import {
  autoUpdate,
  flip,
  offset,
  Placement,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { addDays, endOfMonth, getDay, startOfMonth, subDays } from 'date-fns'
import { useMemo, useState } from 'react'

import { DateRange } from '@/models/helpers'

import { HEADER_DROPDOWN_OFFSET } from './date-picker.constants'
import { CalendarDay } from './date-picker.types'
import {
  getToday,
  isDateDisabled,
  isDateInRange,
  isDateSelected,
  isSameDay,
} from './date-picker.utils'

export const useHeaderDropdown = (placement: Placement = 'bottom-end') => {
  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(HEADER_DROPDOWN_OFFSET),
      flip(),
      sizeMiddleware({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          })
        },
      }),
    ],
    onOpenChange: setIsOpen,
    open: isOpen,
    placement,
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  return {
    floatingStyles,
    getFloatingProps,
    getReferenceProps,
    isOpen,
    refs,
    setIsOpen,
  }
}

interface UseCalendarDaysOptions {
  currentMonth: Date
  value?: Date | DateRange | null
  minDate?: Date
  maxDate?: Date
  rangeStart?: Date | null
  timezone?: string
}

export const useCalendarDays = ({
  currentMonth,
  value,
  minDate,
  maxDate,
  rangeStart,
  timezone,
}: UseCalendarDaysOptions): CalendarDay[] => {
  return useMemo(() => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)

    const daysFromPrevMonth = getDay(monthStart)
    const daysFromNextMonth = 6 - getDay(monthEnd)

    const days: CalendarDay[] = []
    const today = getToday(timezone)

    // Note: When starting a new range selection, don't show the old value
    const displayValue = rangeStart ? null : value

    // Note: Add days from previous month
    const prevMonthStart = subDays(monthStart, daysFromPrevMonth)
    for (let i = 0; i < daysFromPrevMonth; i++) {
      const date = addDays(prevMonthStart, i)
      days.push(createCalendarDay(date, false, today, displayValue, rangeStart, minDate, maxDate))
    }

    // Note: Add days from current month
    let currentDate = monthStart
    while (currentDate <= monthEnd) {
      days.push(
        createCalendarDay(currentDate, true, today, displayValue, rangeStart, minDate, maxDate),
      )
      currentDate = addDays(currentDate, 1)
    }

    // Note: Add days from next month
    for (let i = 1; i <= daysFromNextMonth; i++) {
      const date = addDays(monthEnd, i)
      days.push(createCalendarDay(date, false, today, displayValue, rangeStart, minDate, maxDate))
    }

    return days
  }, [currentMonth, value, minDate, maxDate, rangeStart, timezone])
}

const createCalendarDay = (
  date: Date,
  isCurrentMonth: boolean,
  today: Date,
  displayValue: Date | DateRange | null | undefined,
  rangeStart: Date | null | undefined,
  minDate?: Date,
  maxDate?: Date,
): CalendarDay => {
  const isSelected =
    isDateSelected(date, displayValue) || (rangeStart ? isSameDay(date, rangeStart) : false)

  return {
    date,
    isCurrentMonth,
    isDisabled: isDateDisabled(date, minDate, maxDate),
    isInRange: isDateInRange(date, displayValue),
    isSelected,
    isToday: isSameDay(date, today),
  }
}
